<template>
  <div v-if="!error">
    <template
      v-if="loading"
    >
      <v-skeleton-loader
        v-for="i in 5"
        :key="i"
        type="list-item"
        class="nav-link-skeleton mb-4"
      />
    </template>
    <v-list
      v-else-if="(analyzesLink || navLinks?.length)"
      class="d-flex flex-column ga-2"
      :class="counterXSpaceClass"
    >
      <v-list-item
        v-if="analyzesLink"
        class="primary--text mb-2"
        :class="xSpaceClass"
        @click="handleClickAnalyzes(analyzesLink)"
      >
        <v-list-item-icon class="align-self-center my-0 mr-4">
          <div class="success pa-2 rounded-lg">
            <v-icon color="bg-gray-0">
              {{ analyzesLink.icon }}
            </v-icon>
          </div>
        </v-list-item-icon>
        <v-list-item-content>
          <div>
            <div class="text-subtitle-1 text--text">
              {{ analyzesLink.title }}
            </div>
            <div class="text-body-2 text-secondary--text">
              {{ analyzesLink.subtitle }}
            </div>
          </div>
        </v-list-item-content>
        <v-list-item-icon class="align-self-center">
          <v-icon color="icon-secondary">
            ui-icon-arrow-right
          </v-icon>
        </v-list-item-icon>
      </v-list-item>
      <v-list-item
        v-for="(navLink, i) in navLinks"
        :key="i"
        :href="navLink.link"
        class="primary--text"
        :class="xSpaceClass"
      >
        <v-list-item-icon class="align-self-center my-0 mr-4">
          <div class="bg-primary pa-2 rounded-lg">
            <v-icon color="icon-secondary">
              {{ navLink.icon }}
            </v-icon>
          </div>
        </v-list-item-icon>
        <v-list-item-content>
          <div>
            <div class="text-body-1 text--text">
              {{ navLink.title }}
            </div>
            <div class="text-body-2 text-secondary--text">
              {{ navLink.subtitle }}
            </div>
          </div>
        </v-list-item-content>
        <v-list-item-icon class="align-self-center">
          <v-icon color="icon-secondary">
            ui-icon-arrow-right
          </v-icon>
        </v-list-item-icon>
      </v-list-item>
    </v-list>
    <template
      v-else
    >
      Вы&nbsp;можете искать по&nbsp;фамилии врача,
      названию клиники и&nbsp;услуге
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import apiTownInfo from 'www/vue_components/common/SearchBar/api/apiTownInfo'
import type { ApiTownInfoResponseGet } from 'www/vue_components/common/SearchBar/api/apiTownInfo.types'
import sendYaGoal from 'www/vue_components/common/SearchBar/functions/sendYaGoal'

type NavItem = {
  icon: string
  title: string
  subtitle: string
  link: string
}

const props = defineProps<{
  counterXSpaceClass?: string
  xSpaceClass?: string
  town: {
    id: number
    name: string
    name_pred: string
    translit: string
  }
}>()

const analyzesLink = ref<NavItem>()
const navLinks = ref<NavItem[]>()

const handleClickAnalyzes = (analyzesLink: NavItem) => {
  sendYaGoal('search_click_analyzes')
  window.location.assign(analyzesLink.link)
}

const loading = ref(false)
const error = ref(false)

const townInfo = ref<ApiTownInfoResponseGet>()
const townInfoCache: Map<string, ApiTownInfoResponseGet> = new Map()

const loadTownInfo = async () => {
  error.value = false
  loading.value = true

  try {
    const requestData = {
      town_id: props.town.id,
    }
    const cacheKey = JSON.stringify(requestData)

    if (!townInfoCache.has(cacheKey)) {
      const { data } = await apiTownInfo.get(requestData)

      townInfoCache.set(cacheKey, data)
    }

    townInfo.value = townInfoCache.get(cacheKey) || undefined

    if (!townInfo.value) {
      return
    }

    analyzesLink.value = townInfo.value.analyzes_link
      ? {
        icon: 'ui-icon-syringe-40',
        title: 'Анализы со скидкой 15%',
        subtitle: 'При записи через ПроДокторов',
        link: townInfo.value.analyzes_link,
      }
      : undefined

    navLinks.value = [
      {
        icon: 'ui-icon-administrator-40',
        title: 'Врачи',
        subtitle: townInfo.value.doctors.count.toLocaleString('ru-RU'),
        link: townInfo.value.doctors.link,
      },
      {
        icon: 'ui-icon-hospital-1-40',
        title: 'Клиники',
        subtitle: townInfo.value.lpu.count.toLocaleString('ru-RU'),
        link: townInfo.value.lpu.link,
      },
      {
        icon: 'ui-icon-stetoscope-40',
        title: 'Услуги',
        subtitle: townInfo.value.services.count.toLocaleString('ru-RU'),
        link: townInfo.value.services.link,
      },
      {
        icon: 'ui-icon-like-40',
        title: 'Диагностика',
        subtitle: townInfo.value.diagnostics.count.toLocaleString('ru-RU'),
        link: townInfo.value.diagnostics.link,
      },
      {
        icon: 'ui-icon-box-40',
        title: 'Лечение',
        subtitle: townInfo.value.diseases.count.toLocaleString('ru-RU'),
        link: townInfo.value.diseases.link,
      },
    ].filter(n => n.subtitle !== '0')
  } catch {
    error.value = true
  } finally {
    loading.value = false
  }
}

watch(() => props.town, async () => {
  await loadTownInfo()
}, { immediate: true })
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/variables';

.nav-link-skeleton {
  :deep(.v-skeleton-loader__list-item) {
    height: 56px;
    padding: 0;
  }

  :deep(.v-skeleton-loader__text) {
    height: 56px;
    border-radius: $border-radius-md;
    background: $ui-kit-bg-gray-40;
  }
}
</style>
